import { CartLinesUpdateEvent } from '@shopify/events';

const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

/**
 * Adds one or more lines to the cart and lets Horizon's cart drawer, cart icon
 * and cart items re-render through the standard CartLinesUpdateEvent.
 */
async function add(items, source) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
  const deferred = CartLinesUpdateEvent.createPromise();
  const itemCount = items.reduce((n, i) => n + Number(i.quantity || 1), 0);

  (source || document.body).dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: Number(i.quantity || 1) })),
      promise: deferred.promise,
    })
  );

  const body = { items: items.map((i) => ({ id: Number(i.id), quantity: Number(i.quantity || 1) })) };
  if (sectionIds.length) body.sections = [...new Set(sectionIds)].join(',');

  let response;
  try {
    const res = await fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    });
    response = await res.json();
    const cartRes = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
    const ajaxCart = await cartRes.json();
    const failed = Boolean(response && response.status);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        items: ajaxCart.items,
        source: 'cl-cart',
        sourceId: 'cl-cart',
        itemCount,
        sections: failed ? undefined : response.sections,
        didError: failed,
      },
    });
    if (failed) throw new Error(response.description || response.message || 'Could not add to cart');
    if (!document.querySelector('theme-drawer#cart-drawer')) window.location.href = root + 'cart';
    return ajaxCart;
  } catch (err) {
    if (!response) deferred.reject(err);
    throw err;
  }
}

window.CL = Object.assign(window.CL || {}, { add });

/* Cart drawer upsell (delegated, survives drawer re-renders) */
document.addEventListener('click', async (e) => {
  const btn = e.target instanceof Element ? e.target.closest('[data-cl-upsell-add]') : null;
  if (!btn) return;
  e.preventDefault();
  const wrap = btn.closest('[data-cl-upsell]');
  const select = wrap && wrap.querySelector('select');
  const id = (select && select.value) || btn.dataset.variant;
  if (!id) return;
  btn.disabled = true;
  const label = btn.textContent;
  btn.textContent = '…';
  try {
    await add([{ id, quantity: 1 }], btn);
  } catch (err) {
    console.warn(err);
  } finally {
    btn.disabled = false;
    btn.textContent = label;
  }
});

/* Scroll reveal */
function initReveal() {
  const els = document.querySelectorAll('.cl-reveal:not(.is-in)');
  if (!('IntersectionObserver' in window)) { els.forEach((el) => el.classList.add('is-in')); return; }
  const io = new IntersectionObserver((entries) => {
    entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } });
  }, { rootMargin: '0px 0px -8% 0px' });
  els.forEach((el) => io.observe(el));
}

/* Sticky CTA: shows after the watched element scrolls out of view */
function initSticky() {
  document.querySelectorAll('[data-cl-sticky]').forEach((bar) => {
    const watch = document.querySelector(bar.dataset.watch);
    if (!watch || !('IntersectionObserver' in window)) return;
    new IntersectionObserver(([en]) => {
      bar.classList.toggle('is-on', !en.isIntersecting && en.boundingClientRect.top < 0);
    }).observe(watch);
  });
}

/* Countdown to a real, merchant-set end date. Hidden when unset or expired. */
function initCountdowns() {
  document.querySelectorAll('[data-cl-countdown]').forEach((el) => {
    const end = Date.parse(el.dataset.end || '');
    const out = el.querySelector('[data-cl-time]');
    if (!end || !out) { el.hidden = true; return; }
    let t;
    const tick = () => {
      const ms = end - Date.now();
      if (ms <= 0) { el.hidden = true; clearInterval(t); return; }
      const d = Math.floor(ms / 864e5);
      const h = Math.floor((ms % 864e5) / 36e5);
      const m = Math.floor((ms % 36e5) / 6e4);
      const s = Math.floor((ms % 6e4) / 1e3);
      out.textContent = (d > 0 ? d + 'd ' : '') + String(h).padStart(2, '0') + 'h ' + String(m).padStart(2, '0') + 'm ' + String(s).padStart(2, '0') + 's';
      el.hidden = false;
    };
    t = setInterval(tick, 1000);
    tick();
  });
}

/* Delivery estimate in business days */
function addBusinessDays(date, n) {
  const d = new Date(date);
  let added = 0;
  while (added < n) { d.setDate(d.getDate() + 1); const w = d.getDay(); if (w !== 0 && w !== 6) added++; }
  return d;
}
function initEta() {
  const fmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
  document.querySelectorAll('[data-cl-eta]').forEach((el) => {
    const min = parseInt(el.dataset.min || '0', 10);
    const max = parseInt(el.dataset.max || '0', 10);
    if (!min || !max) return;
    const now = new Date();
    el.textContent = fmt.format(addBusinessDays(now, min)) + ' – ' + fmt.format(addBusinessDays(now, max));
  });
}

function init() { initReveal(); initSticky(); initCountdowns(); initEta(); }
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
document.addEventListener('shopify:section:load', init);
